import React, { useEffect, useRef, useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, Button, Stack } from '@mui/material';
import { Replay as ReplayIcon } from '@mui/icons-material';
import { Outfit } from '../../types';
import { PlaybackSpeed, NarratorVoice, SPEED_DURATIONS } from '../../types/fashion-show';
import { useReelPlayback } from '../../hooks/useReelPlayback';
import { useAudioManager } from '../../hooks/useAudioManager';
import { useNarration } from '../../hooks/useNarration';
import RunwayBackdrop from './RunwayBackdrop';
import OutfitSlide from './OutfitSlide';
import PlaybackControls from './PlaybackControls';

export interface ReelPlayerProps {
  outfits: Outfit[];
  speed: PlaybackSpeed;
  voice: NarratorVoice;
  onComplete: () => void;
  onExit: () => void;
}

const MUSIC_URL = '/assets/runwayMusic.mp3';

/**
 * Full-screen overlay component that handles fashion show reel playback.
 *
 * Wires useReelPlayback, useAudioManager, and useNarration hooks. Composes
 * RunwayBackdrop, OutfitSlide, and PlaybackControls. Manages browser back
 * button via popstate event listener and history.pushState. Coordinates
 * narration playback and pre-fetching per outfit, volume ducking between
 * music and narration, and synchronized pause/resume of both audio streams.
 * Shows a completion screen after the last outfit. Cleans up all timers,
 * audio, and event listeners on unmount.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 5.1, 5.2, 5.3, 5.4,
 *               6.1, 6.2, 6.3, 6.4, 7.2, 7.3, 8.1, 8.2, 8.3, 8.4,
 *               9.1, 9.3, 10.1, 10.4, 10.5, 10.6, 10.7, 10.10
 */
const ReelPlayer: React.FC<ReelPlayerProps> = ({
  outfits,
  speed,
  voice,
  onComplete,
  onExit,
}) => {
  const mountedRef = useRef<boolean>(true);
  const intervalMs = SPEED_DURATIONS[speed];
  const [showCompletion, setShowCompletion] = useState(false);
  // Skip the isPaused effect on initial mount — we don't want to call
  // narration.resume() before narration has even started for the first outfit
  const isPausedEffectMountedRef = useRef<boolean>(false);

  const {
    currentIndex,
    isPaused,
    isComplete,
    togglePause,
    holdAdvance,
    releaseAdvance,
  } = useReelPlayback(outfits.length, intervalMs);

  const music = useAudioManager(MUSIC_URL);

  // Wire useNarration with split callbacks for proper synchronization:
  //
  // 1. handleNarrationFetchStart: called IMMEDIATELY (synchronously) when
  //    playForOutfit begins, BEFORE the async API call to Bedrock/Polly.
  //    This holds outfit advancement so the timer can't fire during the fetch.
  //
  // 2. handleAudioPlaying: called when audio actually starts playing.
  //    This ducks the background music volume.
  //
  // 3. handleNarrationEnd: called when audio finishes, errors, or fetch fails.
  //    This releases the hold and restores music volume.
  const handleNarrationFetchStart = useCallback(() => {
    holdAdvance();
  }, [holdAdvance]);

  const handleNarrationEnd = useCallback(() => {
    music.restoreVolume();
    releaseAdvance();
  }, [music, releaseAdvance]);

  const handleAudioPlaying = useCallback(() => {
    music.duckVolume();
  }, [music]);

  // Narration playback rate: slow = 0.9x, normal = 1.0x, fast = 1.3x
  const narrationRate = speed === 'slow' ? 0.9 : speed === 'fast' ? 1.3 : 1.0;

  const narration = useNarration(handleNarrationFetchStart, handleNarrationEnd, voice, narrationRate, handleAudioPlaying);

  // Start music when the player mounts
  useEffect(() => {
    music.play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync music and narration pause/resume with reel pause state.
  // Skips the initial mount to avoid calling narration.resume() before
  // the first outfit's narration has even started fetching.
  useEffect(() => {
    if (!isPausedEffectMountedRef.current) {
      isPausedEffectMountedRef.current = true;
      return;
    }

    if (isPaused) {
      music.pause();
      narration.pause();
    } else if (!isComplete) {
      music.resume();
      narration.resume();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaused]);

  // Handle reel completion
  useEffect(() => {
    if (isComplete && mountedRef.current) {
      music.stop();
      narration.stop();
      setShowCompletion(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  // Browser back button handling via popstate
  useEffect(() => {
    // Push a state entry so pressing back triggers popstate instead of navigating away
    window.history.pushState({ fashionShowReel: true }, '');

    const handlePopState = (_event: PopStateEvent) => {
      if (mountedRef.current) {
        handleExit();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      music.stop();
      narration.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Coordinate narration per outfit: play for current, prefetch for next
  useEffect(() => {
    if (isComplete || showCompletion || isPaused) return;

    const current = outfits[currentIndex];
    const next = currentIndex + 1 < outfits.length ? outfits[currentIndex + 1] : null;

    if (current) {
      narration.playForOutfit(current);
    }

    if (next) {
      narration.prefetchForOutfit(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const handleExit = useCallback(() => {
    music.stop();
    narration.stop();
    onExit();
  }, [music, narration, onExit]);

  const handleTogglePause = useCallback(() => {
    togglePause();
  }, [togglePause]);

  const currentOutfit = outfits[currentIndex];

  // Completion screen — rendered via Portal to escape parent stacking contexts
  if (showCompletion) {
    return ReactDOM.createPortal(
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack spacing={3} alignItems="center">
          <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
            🎉
          </Typography>
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
            Fashion Show Complete!
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            You viewed {outfits.length} outfit{outfits.length !== 1 ? 's' : ''}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
              startIcon={<ReplayIcon />}
              onClick={onComplete}
            >
              Back to Setup
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExit}
            >
              Return to Feed
            </Button>
          </Stack>
        </Stack>
      </Box>,
      document.body
    );
  }

  // Main playback view — rendered via Portal to escape parent stacking contexts
  return ReactDOM.createPortal(
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* RunwayBackdrop behind outfit slide */}
      <RunwayBackdrop isActive={true} />

      {/* Outfit slide */}
      {currentOutfit && (
        <OutfitSlide outfit={currentOutfit} onClick={handleTogglePause} />
      )}

      {/* Playback controls overlay */}
      <PlaybackControls
        currentIndex={currentIndex}
        totalOutfits={outfits.length}
        isPaused={isPaused}
        isMusicMuted={music.isMuted}
        isNarrationMuted={narration.isMuted}
        musicVolume={music.volume}
        onExit={handleExit}
        onToggleMusicMute={music.toggleMute}
        onToggleNarrationMute={narration.toggleMute}
        onMusicVolumeChange={music.setVolume}
      />
    </Box>,
    document.body
  );
};

export default ReelPlayer;
